import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AmountUtils, cn, CurrencyUtils, FormatUtils, PaletteUtils } from '@shared/utils';
import { AccountStatus, type AccountResponse } from '@shared/api';
import { Button, Icon, Text } from '@shared/widgets';
import ChevronIcon from '@assets/icons/chevron.svg';

interface AccountPaymentTileVcfcProps {
  account: AccountResponse;
  mainAccount?: AccountResponse;
  subAccounts?: AccountResponse[];
  accounts?: boolean;
  // currency?: string;
  selectedAccountId?: number;
  onAccountSwitch?: (accountId: number) => void;
}

export const AccountPaymentTileVcfc = (props: AccountPaymentTileVcfcProps) => {
  const [options, setOptions] = useState<boolean>(true);

  const { t } = useTranslation('accounts');

  const handleArrowClick = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    setOptions(!options);
  };

  return (
    <div className='flex h-full flex-col overflow-x-auto'>
      <div className={cn('flex flex-col rounded-tl-lg rounded-tr-lg bg-white p-5 dark:bg-soft-black', !props.accounts && 'gap-3')}>
        <div className='flex items-center justify-between'>
          <Text size='3xl'>{AmountUtils.transformToAmountWithCurrency(props.account.availableBalance ?? 0, props.account.currency)}</Text>
          <div
            className={cn(
              'flex h-[29px] min-w-[65px] items-center justify-center gap-2 rounded-[45px] px-2 text-white',
              props.account.status === AccountStatus.Active && 'bg-primary',
              ([AccountStatus.Blocked, AccountStatus.Delinquent] as AccountStatus[]).includes(props.account.status) && 'bg-red',
              ([AccountStatus.Closed, AccountStatus.Dormant] as AccountStatus[]).includes(props.account.status) && 'bg-light/70',
            )}
          >
            <Text weight='medium' color='white'>
              {CurrencyUtils.getSymbolByCode(props.account.currency)}
            </Text>
            <Text className='text-[10px]' weight='semibold' color='white'>
              {t(`statuses.account.${props.account.status}`, { ns: 'common' })}
            </Text>
          </div>
        </div>
        {!props.accounts && (
          <div className='flex gap-2'>
            <Button
              className={cn(
                'rounded border px-4 py-2 text-xs font-semibold uppercase',
                props.account.mainAccId === null
                  ? props.selectedAccountId === props.account.id
                    ? 'bg-primary text-white'
                    : 'border-light-gray text-light-gray'
                  : 'border-light-gray text-light-gray',
              )}
              color='icon'
              disabled={props.mainAccount === undefined}
              onClick={() => props.onAccountSwitch!(props.account.mainAccId === null ? props.account.id : props.account.mainAccId)}
            >
              {CurrencyUtils.getSymbolByCode(props.mainAccount ? props.mainAccount.currency : '')} {t('accountType.main', { ns: 'common' })}
            </Button>
            {props.subAccounts?.map((subAccount) => (
              <Button
                key={subAccount.id}
                className={cn(
                  'rounded border px-4 py-2 text-xs font-semibold uppercase',
                  props.selectedAccountId === subAccount.id ? 'bg-primary text-white' : 'border-light-gray text-light-gray',
                )}
                color='icon'
                onClick={() => props.onAccountSwitch!(subAccount.id)}
              >
                {CurrencyUtils.getSymbolByCode(subAccount.currency)} {t('accountType.sub', { ns: 'common' })}
              </Button>
            ))}
          </div>
        )}
        <div className='mt-2 flex flex-col gap-2'>
          <Text weight='medium' color='black'>
            {props.account.name || FormatUtils.formatAccountNumber(props.account.accountNumber)}
          </Text>
          <Text className='text-[10px]' weight='semibold' color='secondary-light'>
            {props.account.productName}
          </Text>
        </div>
        <hr className='my-4 text-slate/15' />
        <div className='flex flex-col gap-3'>
          <div className='flex w-full justify-between'>
            <Text weight='medium' size='xs'>
              {t('payment.tile.fields.accountNumber')}
            </Text>
            <Text weight='semibold' size='xs'>
              {props.account.accountNumber ? '***' + props.account.accountNumber.slice(-4) : 'No Data'}
            </Text>
          </div>
          <div className='flex w-full justify-between'>
            <Text weight='medium' size='xs'>
              {t('payment.tile.fields.iban')}
            </Text>
            <div className='flex items-center gap-1'>
              <Text weight='semibold' size='xs'>
                {props.account.iban}
              </Text>
            </div>
          </div>
        </div>
      </div>
      {props.accounts && !!props.subAccounts?.length && (
        <div className={cn('rounded-lg bg-primary p-5 pt-0', !options && 'pb-0')}>
          <div className='flex min-h-12 cursor-pointer items-center justify-between px-0 py-4'>
            <Text weight='medium' size='xs' color='secondary-light'>
              {t('payment.tile.otherCurrencies.title')}
            </Text>
            <div className='flex gap-1'>
              {props.subAccounts &&
                props.subAccounts.length > 0 &&
                props.subAccounts.map((c) => (
                  <div key={c.id} className='flex h-5 items-center rounded border bg-white px-1.5 text-xs font-semibold text-light-gray'>
                    {CurrencyUtils.getSymbolByCode(c.currency)}
                  </div>
                ))}
              <span
                onClick={handleArrowClick}
                className={cn(
                  'mx-2 flex h-4 w-4 flex-none items-center justify-center text-center text-xl text-slate transition-transform duration-200 dark:text-gray',
                  options ? 'rotate-90' : '-rotate-90',
                )}
              >
                <Icon icon={<ChevronIcon className='text-white' />} />
              </span>
            </div>
          </div>
          <div className={cn(!options ? 'hidden' : 'block pt-3')}>
            {props.subAccounts?.length > 0 && (
              <div>
                {props.subAccounts.map((subAccount) => (
                  <div
                    key={subAccount.id}
                    className={cn(
                      'mb-2 flex min-h-[52px] items-center gap-3 bg-dark-graphite px-3 py-2 dark:bg-slate',
                      PaletteUtils.isPersonalPalette && 'bg-primary-700',
                    )}
                  >
                    <div className='flex h-full min-h-[34px] min-w-[68px] items-center justify-center rounded border bg-white/70 px-2 uppercase text-primary'>
                      <span className='ml-1 mr-2.5 text-base dark:text-white'>{CurrencyUtils.getSymbolByCode(subAccount.currency)}</span>
                      <Text className='mt-0.5' uppercase weight='semibold' size='xs' color='primary'>
                        {t('accountType.type.sub', { ns: 'common' })}
                      </Text>
                    </div>
                    <div>
                      <Text size='xs' color='secondary-light'>
                        {t('payment.tile.otherCurrencies.fields.accountBalance')}
                      </Text>
                      <Text weight='semibold' size='xs' color='white'>
                        {AmountUtils.transformToAmountWithCurrency(subAccount.availableBalance ?? 0, subAccount.currency)}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
